import React, {useEffect, useMemo, useRef, useState} from "react";
import styled from "styled-components";

export default function FilterCheckboxDropdown({
  options = [],
  selectedValues = [],
  onChange,
  disabled = false,
  itemName,
  searchPlaceholder,
  emptyMessage = "검색 결과가 없습니다.",
}) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const containerRef = useRef(null);
  const selectableOptions = useMemo(
      () => options.filter(option => option.value !== "" && option.value != null),
      [options]
  );
  const selectedOptions = selectableOptions.filter(option => selectedValues.includes(option.value));
  const normalizedKeyword = keyword.trim().toLowerCase();
  const filteredOptions = selectableOptions.filter(option =>
      String(option.label).toLowerCase().includes(normalizedKeyword)
  );
  const isAllSelected = selectableOptions.length > 0
      && selectedOptions.length === selectableOptions.length;
  const displayValue = selectedOptions.length === 0
      ? "전체"
      : selectedOptions.length === 1
          ? selectedOptions[0].label
          : `${selectedOptions.length}개 ${itemName} 선택`;

  useEffect(() => {
    const handleOutsideClick = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = event => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const toggleOption = option => {
    const nextValues = selectedValues.includes(option.value)
        ? selectedValues.filter(value => value !== option.value)
        : [...selectedValues, option.value];
    onChange(selectableOptions.filter(item => nextValues.includes(item.value)));
  };

  const toggleAll = () => onChange(isAllSelected ? [] : selectableOptions);

  return (
      <Container ref={containerRef}>
        <Trigger
            type="button"
            disabled={disabled}
            $open={open}
            onClick={() => !disabled && setOpen(current => !current)}
            aria-haspopup="listbox"
            aria-expanded={open}
        >
          <span>{displayValue}</span>
          <DropdownArrow $open={open} aria-hidden="true">⌄</DropdownArrow>
        </Trigger>
        {open && (
            <Menu>
              <SearchInput
                  value={keyword}
                  onChange={event => setKeyword(event.target.value)}
                  placeholder={searchPlaceholder}
                  autoFocus
              />
              <Option type="button" onClick={toggleAll}>
                <input type="checkbox" checked={isAllSelected} readOnly/>
                <strong>전체 선택</strong>
              </Option>
              <OptionList role="listbox" aria-multiselectable="true">
                {filteredOptions.map(option => (
                    <Option
                        type="button"
                        key={option.value}
                        onClick={() => toggleOption(option)}
                        role="option"
                        aria-selected={selectedValues.includes(option.value)}
                    >
                      <input type="checkbox" checked={selectedValues.includes(option.value)} readOnly/>
                      <span>{option.label}</span>
                    </Option>
                ))}
                {!filteredOptions.length && <EmptyOption>{emptyMessage}</EmptyOption>}
              </OptionList>
            </Menu>
        )}
      </Container>
  );
}

const Container = styled.div`position:relative;width:100%;`;
const Trigger = styled.button`
  display:flex;width:100%;height:38px;box-sizing:border-box;align-items:center;justify-content:space-between;
  padding:0 9px 0 11px;border:1px solid ${({$open})=>$open?"var(--color-primary)":"var(--color-border-strong)"};border-radius:7px;
  background:${({disabled})=>disabled?"#f5f6f7":"var(--color-surface)"};color:${({disabled})=>disabled?"#a5adb6":"var(--color-text)"};
  font-family:inherit;font-size: var(--font-size-13);text-align:left;cursor:${({disabled})=>disabled?"not-allowed":"pointer"};
  box-shadow:${({$open})=>$open?"0 0 0 3px rgba(59,130,246,.1)":"none"};
  &:hover{border-color:${({disabled})=>disabled?"var(--color-border-strong)":"var(--color-primary-focus)"};}
  >span:first-child{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
`;
const DropdownArrow = styled.span`
  flex:0 0 auto;color:#7c8795;font-size: var(--font-size-18);transform:${({$open})=>$open?"rotate(180deg)":"none"};transition:transform .15s ease;
`;
const Menu = styled.div`
  position:absolute;z-index:50;top:calc(100% + 5px);right:0;left:0;overflow:hidden;box-sizing:border-box;padding:7px;
  border:1px solid var(--color-border-strong);border-radius:7px;background:var(--color-surface);box-shadow:0 10px 28px rgba(31,41,55,.12);
`;
const SearchInput = styled.input`
  width:100%;height:34px;box-sizing:border-box;margin-bottom:6px;padding:0 10px;border:1px solid var(--color-border-strong);
  border-radius:5px;color:var(--color-text);font-family:inherit;font-size: var(--font-size-14);outline:none;
  &:focus{border-color:var(--color-primary);box-shadow:0 0 0 2px rgba(59,130,246,.1);}
`;
const OptionList = styled.div`max-height:220px;overflow-y:auto;border-top:1px solid var(--color-divider);padding-top:4px;`;
const Option = styled.button`
  display:flex;width:100%;min-height:34px;box-sizing:border-box;align-items:center;gap:8px;padding:6px 8px;
  border:0;border-radius:5px;background:var(--color-surface);color:#4b5563;font-family:inherit;font-size: var(--font-size-14);text-align:left;cursor:pointer;
  &:hover{background:var(--color-primary-soft);color:var(--color-primary-strong);}
  input{width:15px;height:15px;flex:0 0 15px;margin:0;accent-color:var(--color-primary);pointer-events:none;}
  strong{font-weight:700;} span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
`;
const EmptyOption = styled.div`padding:14px 8px;color:#929ba5;font-size: var(--font-size-14);text-align:center;`;
